import axios from "axios";

console.log("🚀 [LETTERLY] Lark Service — Proxy Mode");

// ── Lark Table IDs ────────────────────────────────────────────
export const LARK_TABLES = {
  MEETINGS:     "tbl9EwkUiGuLB7Gt",
  TRANSLATIONS: "tblamX3qXIOQikjZ",
  SUMMARIES:    "tblT8YAtINEoBNNx",
};

// ── Core proxy caller ─────────────────────────────────────────
export async function callLarkProxy(method, path, data = null) {
  const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const proxyUrl = isLocal
      ? "http://localhost:4000/proxy"
      : "/api/proxy";

  try {
    console.log(`📡 [LARK] ${method} ${path}`);
    const response = await axios.post(proxyUrl, { method, path, data });
    
    // Safety check for HTML responses (which cause JSON parse errors)
    if (typeof response.data === 'string' && response.data.trim().startsWith("<!DOCTYPE")) {
      throw new Error("Lark Database returned an HTML error page instead of data. This usually means a Table ID or Base Token is incorrect.");
    }
    
    return response.data;
  } catch (error) {
    const errorData = error.response?.data;
    const errorMessage = errorData?.error || errorData?.message || error.message;
    console.error("❌ [LARK] Error:", errorMessage);
    
    if (errorMessage.includes("Unexpected token") || (typeof errorData === 'string' && errorData.includes("<!DOCTYPE"))) {
      throw new Error("Lark Database connection failed (received HTML). Check your Lark Table IDs in the backend .env file.");
    }
    
    throw new Error(errorMessage);
  }
}

// ── Generic CRUD ──────────────────────────────────────────────
export const createRecord = async (tableId, fields) => {
  const res = await callLarkProxy("POST", `/tables/${tableId}/records`, { fields });
  return res.data?.record?.record_id;
};

export const updateRecord = async (tableId, recordId, fields) => {
  await callLarkProxy("PUT", `/tables/${tableId}/records/${recordId}`, { fields });
};

export const deleteRecord = async (tableId, recordId) => {
  await callLarkProxy("DELETE", `/tables/${tableId}/records/${recordId}`);
};

export const searchRecords = async (tableId, filterConditions) => {
  const res = await callLarkProxy("POST", `/tables/${tableId}/records/search`, {
    filter: { conjunction: "and", conditions: filterConditions },
  });
  // Normalize each item's rich-text fields to plain values (see normalizeLarkField).
  return (res.data?.items || []).map((item) => ({
    ...item,
    fields: normalizeFields(item.fields),
  }));
};

export const listRecords = async (tableId, pageSize = 100) => {
  const res = await callLarkProxy("GET", `/tables/${tableId}/records?page_size=${pageSize}`);
  return res.data?.items || [];
};

// ── Meeting helpers ───────────────────────────────────────────

export const insertMeeting = async (meeting) => {
  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  const professionalTranscript = (meeting.transcript || [])
    .map(s => `[${formatTime(s.start)}] ${s.speaker}: ${s.text}`)
    .join('\n');

  return await createRecord(LARK_TABLES.MEETINGS, {
    "Text":          meeting.title || "New Meeting",
    "Meeting ID":    meeting.meeting_id,
    "Title":         meeting.title || "",
    "Status":        meeting.status || "recording",
    "Full Text":     meeting.full_text || "",
    "Language":      meeting.language || "",
    "Segment Count": meeting.segment_count || 0,
    "Created At":    Date.now(),
    "Transcript JSON": JSON.stringify(meeting.transcript || []),
    "Professional Transcript": professionalTranscript,
  });
};

// Lark's search API returns text fields as an array of rich-text runs
// (e.g. [{ text: "...", type: "text" }]) while the list API returns plain
// strings. Normalize both to a plain string so callers can rely on the type.
export const normalizeLarkField = (val) => {
  if (val == null) return val;
  if (typeof val === "string" || typeof val === "number") return val;
  if (Array.isArray(val)) {
    return val.map((r) => (r && typeof r === "object" && "text" in r ? r.text : String(r))).join("");
  }
  if (typeof val === "object" && "text" in val) return val.text;
  return val;
};

const normalizeFields = (fields = {}) => {
  const out = {};
  for (const k of Object.keys(fields)) out[k] = normalizeLarkField(fields[k]);
  return out;
};

export const getMeetingByField = async (meetingId) => {
  const items = await searchRecords(LARK_TABLES.MEETINGS, [
    { field_name: "Meeting ID", operator: "is", value: [meetingId] },
  ]);
  if (!items.length) return null;
  const item = items[0];
  return { _recordId: item.record_id, ...normalizeFields(item.fields) };
};

export const getAllMeetings = async () => {
  const items = await listRecords(LARK_TABLES.MEETINGS);
  return items
    .map((item) => ({
      _recordId:      item.record_id,
      meeting_id:     item.fields["Meeting ID"] || "",
      title:          item.fields["Title"] || "",
      status:         item.fields["Status"] || "unknown",
      full_text:      item.fields["Full Text"] || "",
      language:       item.fields["Language"] || "",
      segment_count:  item.fields["Segment Count"] || 0,
      created_at:     item.fields["Created At"] || 0,
      transcript:     JSON.parse(item.fields["Transcript JSON"] || "[]"),
    }))
    .sort((a, b) => b.created_at - a.created_at);
};

export const updateMeetingStatus = async (meetingId, status) => {
  const rec = await getMeetingByField(meetingId);
  if (!rec?._recordId) return;
  await updateRecord(LARK_TABLES.MEETINGS, rec._recordId, { Status: status });
};

export const saveMeetingTranscript = async (meetingId, fullText, language, segments) => {
  const rec = await getMeetingByField(meetingId);
  if (!rec?._recordId) return;

  // Generate a professional formatted transcript string
  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  const professionalTranscript = segments
    .map(s => `[${formatTime(s.start)}] ${s.speaker}: ${s.text}`)
    .join('\n');

  await updateRecord(LARK_TABLES.MEETINGS, rec._recordId, {
    "Text":          rec["Title"] || "Meeting Updated",
    "Full Text":     fullText,
    "Language":      language,
    "Segment Count": segments.length,
    "Status":        "completed",
    "Transcript JSON": JSON.stringify(segments),
    "Professional Transcript": professionalTranscript,
  });
};

// ── Translation helpers ───────────────────────────────────────

export const saveTranslation = async (meetingId, langCode, translatedText) => {
  // Upsert: find existing or create new
  const existing = await searchRecords(LARK_TABLES.TRANSLATIONS, [
    { field_name: "Meeting ID",    operator: "is", value: [meetingId] },
    { field_name: "Language Code", operator: "is", value: [langCode] },
  ]);

  const fields = {
    "Meeting ID":      meetingId,
    "Language Code":   langCode,
    "Translated Text": translatedText,
  };

  if (existing.length) {
    await updateRecord(LARK_TABLES.TRANSLATIONS, existing[0].record_id, fields);
  } else {
    await createRecord(LARK_TABLES.TRANSLATIONS, fields);
  }
};

export const getTranslationsForMeeting = async (meetingId) => {
  const items = await searchRecords(LARK_TABLES.TRANSLATIONS, [
    { field_name: "Meeting ID", operator: "is", value: [meetingId] },
  ]);
  const result = {};
  items.forEach((item) => {
    const lang = item.fields["Language Code"];
    const text = item.fields["Translated Text"];
    if (lang) result[lang] = text;
  });
  return result;
};

// ── Summary helpers ───────────────────────────────────────────

export const saveSummary = async (meetingId, summary) => {
  const existing = await searchRecords(LARK_TABLES.SUMMARIES, [
    { field_name: "Meeting ID", operator: "is", value: [meetingId] },
  ]);

  const fields = {
    "Meeting ID":      meetingId,
    "Key Points":      JSON.stringify(summary.key_points || []),
    "Decisions":       JSON.stringify(summary.decisions || []),
    "Action Items":    JSON.stringify(summary.action_items || []),
    "Overall Summary": summary.overall_summary || "",
  };

  if (existing.length) {
    await updateRecord(LARK_TABLES.SUMMARIES, existing[0].record_id, fields);
  } else {
    await createRecord(LARK_TABLES.SUMMARIES, fields);
  }
};

export const getSummaryForMeeting = async (meetingId) => {
  const items = await searchRecords(LARK_TABLES.SUMMARIES, [
    { field_name: "Meeting ID", operator: "is", value: [meetingId] },
  ]);
  if (!items.length) return {};
  const f = items[0].fields;
  try {
    return {
      key_points:      JSON.parse(f["Key Points"] || "[]"),
      decisions:       JSON.parse(f["Decisions"] || "[]"),
      action_items:    JSON.parse(f["Action Items"] || "[]"),
      overall_summary: f["Overall Summary"] || "",
    };
  } catch {
    return {};
  }
};

export const deleteMeeting = async (meetingId) => {
  const rec = await getMeetingByField(meetingId);
  if (!rec?._recordId) return false;
  await deleteRecord(LARK_TABLES.MEETINGS, rec._recordId);
  return true;
};
